import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'

// AuthCallback is kept eager — it runs immediately after Google OAuth redirect
import AuthCallback from '../auth/AuthCallback'

// All page-level components are lazy so Vite splits them into separate chunks.
// The initial bundle only contains React, React Router, and AuthContext.
const LoginPage      = lazy(() => import('../../pages/LoginPage'))
const LandingPage    = lazy(() => import('../../pages/LandingPage'))
const AppShell       = lazy(() => import('../layout/AppShell'))
const HomePage       = lazy(() => import('../../pages/HomePage'))
const ReportsPage    = lazy(() => import('../../pages/ReportsPage'))

const AdminShell     = lazy(() => import('../../admin/AdminShell'))
const OrgAdminShell  = lazy(() => import('../../org-admin/OrgAdminShell'))
const PrivacyPage    = lazy(() => import('../../pages/PrivacyPage'))
const TermsPage      = lazy(() => import('../../pages/TermsPage'))

const AssetRegistry      = lazy(() => import('../../modules/assets/AssetRegistry'))
const LocationRegistry   = lazy(() => import('../../modules/locations/LocationRegistry'))
const MapApp             = lazy(() => import('../../modules/map/MapApp'))

const InspectHome       = lazy(() => import('../../modules/inspect/InspectHome'))
const InspectionHub     = lazy(() => import('../../modules/inspect/InspectionHub'))

const AssetInspectionList    = lazy(() => import('../../modules/inspect/asset/AssetInspectionList'))
const AssetInspectionSession = lazy(() => import('../../modules/inspect/asset/AssetInspectionSession'))

const GridInspectSession   = lazy(() => import('../../modules/inspect/grid/GridInspectSession'))
const InspectionReportPage = lazy(() => import('../../modules/inspect/InspectionReportPage'))

const InspectByAsset         = lazy(() => import('../../modules/inspect/InspectByAsset'))
const InspectByLocation      = lazy(() => import('../../modules/inspect/InspectByLocation'))
const InspectWorkflowList    = lazy(() => import('../../modules/inspect/InspectWorkflowList'))

const InspectChecklistList   = lazy(() => import('../../modules/inspect/checklist/InspectChecklistList'))
const InspectChecklistFill   = lazy(() => import('../../modules/inspect/checklist/InspectChecklistFill'))
const InspectChecklistDetail = lazy(() => import('../../modules/inspect/checklist/InspectChecklistDetail'))
const ChecklistReportPage    = lazy(() => import('../../modules/inspect/checklist/ChecklistReportPage'))

const PatrolHome    = lazy(() => import('../../modules/patrol/PatrolHome'))
const PatrolSession = lazy(() => import('../../modules/patrol/PatrolSession'))

const EnvHome          = lazy(() => import('../../modules/env/EnvHome'))
const EnvSurveyList    = lazy(() => import('../../modules/env/EnvSurveyList'))
const EnvSurveySession = lazy(() => import('../../modules/env/EnvSurveySession'))

const BlueprintHub            = lazy(() => import('../../modules/blueprint/BlueprintHub'))
const BlueprintTemplateEditor = lazy(() => import('../../modules/blueprint/BlueprintTemplateEditor'))
const BlueprintTemplateViewer = lazy(() => import('../../modules/blueprint/BlueprintTemplateViewer'))
const GridTemplateEditor      = lazy(() => import('../../org-admin/pages/OrgAdminGridTemplateEditor'))

const PtwHome            = lazy(() => import('../../modules/ptw/PtwHome'))
const PtwList            = lazy(() => import('../../modules/ptw/PtwList'))
const PtwCreate          = lazy(() => import('../../modules/ptw/PtwCreate'))
const PtwDetail          = lazy(() => import('../../modules/ptw/PtwDetail'))
const PtwEdit            = lazy(() => import('../../modules/ptw/PtwEdit'))
const PtwReportPage      = lazy(() => import('../../modules/ptw/PtwReportPage'))

const GaisReportPage     = lazy(() => import('../../modules/inspect/asset/GaisReportPage'))
const EnvSurveyReportPage = lazy(() => import('../../modules/env/EnvSurveyReportPage'))
const PatrolReportPage   = lazy(() => import('../../modules/patrol/PatrolReportPage'))

const HelpShell          = lazy(() => import('../../help/HelpShell'))

const PlanningHome   = lazy(() => import('../../modules/planning/PlanningHome'))
const ProjectList    = lazy(() => import('../../modules/planning/ProjectList'))
const ProjectGantt   = lazy(() => import('../../modules/planning/ProjectGantt'))

const SchedulingHome = lazy(() => import('../../modules/scheduling/SchedulingHome'))

const CctvHub          = lazy(() => import('../../modules/cctv/CctvHub'))
const CctvCameraDetail = lazy(() => import('../../modules/cctv/CctvCameraDetail'))
const CctvSettings     = lazy(() => import('../../modules/cctv/CctvSettings'))

export function PrivateRoute({ children }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <SplashScreen/>
  if (!user) {
    // The bare root path is the public marquee, not a protected route —
    // every other path under "/" still bounces to login as before.
    if (location.pathname === '/') return <LandingPage/>
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace/>
  }
  return children
}

export function PublicRoute({ children }) {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (!loading && user) {
    const params = new URLSearchParams(location.search)
    const next = params.get('next')
    return <Navigate to={next && next.startsWith('/') ? next : '/'} replace/>
  }
  return children
}

function FeatureRoute({ feature, children }) {
  const { hasFeature } = useAuth()
  if (!hasFeature(feature)) return (
    <div className="p-6 text-center">
      <div className="text-4xl mb-3">🔒</div>
      <div className="text-white font-semibold mb-1">Feature not available</div>
      <div className="text-slate-400 text-sm">
        Your organisation does not have access to this feature.
        <br/>Contact your administrator to upgrade.
      </div>
    </div>
  )
  return children
}

function SplashScreen() {
  return (
    <div className="fixed inset-0 bg-slate-900 flex items-center justify-center">
      <div className="text-center">
        <div className="text-3xl font-bold text-blue-400 mb-2">Edge4Field</div>
        <div className="text-slate-400 text-sm">Loading…</div>
      </div>
    </div>
  )
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <Suspense fallback={<SplashScreen/>}>
      <Routes>
        {/* Public */}
        <Route path="/login"          element={<PublicRoute><LoginPage/></PublicRoute>}/>
        <Route path="/auth/callback"  element={<AuthCallback/>}/>
        <Route path="/privacy"        element={<PrivacyPage/>}/>
        <Route path="/terms"          element={<TermsPage/>}/>

        {/* Protected */}
        <Route path="/" element={<PrivateRoute><AppShell/></PrivateRoute>}>
          <Route index element={<HomePage/>}/>

        <Route path="vehicles"   element={<Navigate to="/assets" replace />}/>
        <Route path="assets"     element={<FeatureRoute feature="inspect"><AssetRegistry/></FeatureRoute>}/>
        <Route path="locations"  element={<FeatureRoute feature="inspect"><LocationRegistry/></FeatureRoute>}/>
        <Route path="map"        element={<FeatureRoute feature="inspect"><MapApp/></FeatureRoute>}/>

          {/* Inspect module */}
          <Route path="inspect" element={
            <FeatureRoute feature="inspect"><InspectHome/></FeatureRoute>
          }/>

          {/* Unified Inspection sub-hub */}
          <Route path="inspect/inspection" element={<InspectionHub/>}/>

          <Route path="inspect/by-asset" element={<InspectByAsset/>}/>
          <Route path="inspect/by-location" element={<InspectByLocation/>}/>
          <Route path="inspect/workflows" element={
            <FeatureRoute feature="blueprint"><InspectWorkflowList/></FeatureRoute>
          }/>

          <Route path="inspect/grid" element={<Navigate to="/inspect/grid-inspections" replace/>}/>
          <Route path="inspect/asset" element={<Navigate to="/inspect/grid-inspections" replace/>}/>

          <Route path="inspect/grid/:id" element={
            <FeatureRoute feature="inspect_grid"><GridInspectSession/></FeatureRoute>
          }/>

          {/* Grid Based Inspection (GAIS) */}
          <Route path="inspect/grid-inspections" element={
            <FeatureRoute feature="inspect_grid"><AssetInspectionList/></FeatureRoute>
          }/>
          <Route path="inspect/grid-inspections/:id" element={
            <FeatureRoute feature="inspect_grid"><AssetInspectionSession/></FeatureRoute>
          }/>

          {/* Patrol module */}
          <Route path="inspect/patrol" element={
            <FeatureRoute feature="inspect_walkaround"><PatrolHome /></FeatureRoute>
          }/>
          <Route path="inspect/patrol/:id" element={
            <FeatureRoute feature="inspect_walkaround"><PatrolSession /></FeatureRoute>
          }/>

          {/* Environmental & Field Surveys module */}
          <Route path="inspect/env" element={
            <FeatureRoute feature="inspect_pretrip"><EnvHome /></FeatureRoute>
          }/>
          <Route path="inspect/env/:typeSlug" element={
            <FeatureRoute feature="inspect_pretrip"><EnvSurveyList /></FeatureRoute>
          }/>
          <Route path="inspect/env/:typeSlug/:id" element={
            <FeatureRoute feature="inspect_pretrip"><EnvSurveySession /></FeatureRoute>
          }/>

          {/* Inspect checklist module */}
          <Route path="inspect/checklists" element={
            <FeatureRoute feature="inspect_checklist"><InspectChecklistList/></FeatureRoute>
          }/>
          <Route path="inspect/checklists/new" element={
            <FeatureRoute feature="inspect_checklist"><InspectChecklistFill/></FeatureRoute>
          }/>
          <Route path="inspect/checklists/:id/fill" element={
            <FeatureRoute feature="inspect_checklist"><InspectChecklistFill/></FeatureRoute>
          }/>
          <Route path="inspect/checklists/:id" element={
            <FeatureRoute feature="inspect_checklist"><InspectChecklistDetail/></FeatureRoute>
          }/>

          {/* Stubs */}
          <Route path="audit"   element={<ComingSoon title="Audit"/>}/>
          <Route path="quality" element={<ComingSoon title="Quality"/>}/>
          <Route path="reports" element={<FeatureRoute feature="reports"><ReportsPage/></FeatureRoute>}/>
          <Route path="profile" element={<ProfilePage/>}/>

          {/* Blueprint module */}
          <Route path="blueprint" element={
            <FeatureRoute feature="blueprint"><BlueprintHub /></FeatureRoute>
          }/>
          <Route path="blueprint/new" element={
            <FeatureRoute feature="blueprint"><BlueprintTemplateEditor /></FeatureRoute>
          }/>
          <Route path="blueprint/:id/edit" element={
            <FeatureRoute feature="blueprint"><BlueprintTemplateEditor /></FeatureRoute>
          }/>
          <Route path="blueprint/:id" element={
            <FeatureRoute feature="blueprint"><BlueprintTemplateViewer /></FeatureRoute>
          }/>

          {/* Grid Inspection template editor — lives under Blueprint in the main app shell */}
          <Route path="blueprint/grid-templates/new" element={
            <FeatureRoute feature="inspect_grid"><GridTemplateEditor /></FeatureRoute>
          }/>
          <Route path="blueprint/grid-templates/:id" element={
            <FeatureRoute feature="inspect_grid"><GridTemplateEditor /></FeatureRoute>
          }/>

          {/* Planning module */}
          <Route path="planning" element={
            <FeatureRoute feature="planning"><PlanningHome/></FeatureRoute>
          }/>
          <Route path="planning/projects" element={
            <FeatureRoute feature="planning"><ProjectList/></FeatureRoute>
          }/>
          <Route path="planning/projects/:id" element={
            <FeatureRoute feature="planning"><ProjectGantt/></FeatureRoute>
          }/>

          {/* Scheduling module */}
          <Route path="scheduling" element={
            <FeatureRoute feature="scheduling"><SchedulingHome/></FeatureRoute>
          }/>

          {/* PTW module */}
          <Route path="ptw" element={
            <FeatureRoute feature="ptw"><PtwHome/></FeatureRoute>
          }/>
          <Route path="ptw/permits" element={
            <FeatureRoute feature="ptw"><PtwList/></FeatureRoute>
          }/>
          <Route path="ptw/permits/new" element={
            <FeatureRoute feature="ptw"><PtwCreate/></FeatureRoute>
          }/>
          <Route path="ptw/permits/:id" element={
            <FeatureRoute feature="ptw"><PtwDetail/></FeatureRoute>
          }/>
          <Route path="ptw/permits/:id/edit" element={
            <FeatureRoute feature="ptw"><PtwEdit/></FeatureRoute>
          }/>

          {/* CCTV Health */}
          <Route path="cctv" element={
            <FeatureRoute feature="cctv_health"><CctvHub/></FeatureRoute>
          }/>
          <Route path="cctv/settings" element={
            <FeatureRoute feature="cctv_health"><CctvSettings/></FeatureRoute>
          }/>
          <Route path="cctv/:id" element={
            <FeatureRoute feature="cctv_health"><CctvCameraDetail/></FeatureRoute>
          }/>
        </Route>

        {/* Standalone report pages — no AppShell */}
        <Route path="/inspect/grid/:id/report" element={
          <PrivateRoute><InspectionReportPage /></PrivateRoute>
        }/>
        <Route path="/inspect/checklists/:id/report" element={
          <PrivateRoute><ChecklistReportPage /></PrivateRoute>
        }/>
        <Route path="/inspect/grid-inspections/:id/report" element={
          <PrivateRoute><GaisReportPage /></PrivateRoute>
        }/>
        <Route path="/inspect/env/:typeSlug/:id/report" element={
          <PrivateRoute><EnvSurveyReportPage /></PrivateRoute>
        }/>
        <Route path="/inspect/patrol/:id/report" element={
          <PrivateRoute><PatrolReportPage /></PrivateRoute>
        }/>
        <Route path="/ptw/permits/:id/report" element={
          <PrivateRoute><PtwReportPage /></PrivateRoute>
        }/>

        {/* Super admin */}
        <Route path="/admin/*" element={
          <PrivateRoute><AdminShell/></PrivateRoute>
        }/>

        {/* Org admin */}
        <Route path="/org-admin/*" element={
          <PrivateRoute><OrgAdminShell/></PrivateRoute>
        }/>

        {/* Help — no auth required */}
        <Route path="/help/*" element={<HelpShell/>}/>
        <Route path="/help"   element={<HelpShell/>}/>

        <Route path="*" element={<Navigate to="/" replace/>}/>
      </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

function ComingSoon({ title }) {
  return (
    <div className="p-6 text-center py-16">
      <div className="text-4xl mb-3">🚧</div>
      <div className="text-white font-semibold mb-1">{title}</div>
      <div className="text-slate-400 text-sm">Coming soon</div>
    </div>
  )
}

function ProfilePage() {
  const { user, logout } = useAuth()
  return (
    <div className="p-4">
      <h1 className="text-xl font-bold text-white mb-6">Profile</h1>
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 mb-4">
        <div className="flex items-center gap-3 mb-4">
          {user?.avatarUrl
            ? <img src={user.avatarUrl} className="w-12 h-12 rounded-full"/>
            : <div className="w-12 h-12 rounded-full bg-slate-600 flex items-center justify-center text-xl">👤</div>
          }
          <div>
            <div className="font-semibold text-white">{user?.name}</div>
            <div className="text-sm text-slate-400">{user?.email}</div>
            <div className="text-xs text-slate-500 mt-0.5 capitalize">{user?.role?.replace('_',' ')}</div>
          </div>
        </div>
      </div>
      <button onClick={logout}
        className="w-full py-3 rounded-xl border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors text-sm font-medium">
        Sign Out
      </button>
    </div>
  )
}